/**
 * The current-user context channel and its accessor.
 *
 * These live apart from CurrentUserProvider because React Fast Refresh can
 * only hot-swap a module whose every export is a component. A context object
 * or a hook sitting beside the provider makes the whole file unrefreshable,
 * so every edit re-executes the provider's consumers and discards their
 * state instead of patching the component in place.
 */

import { createContext, useContext } from 'react'
import type { MeResponse } from '@/types/auth'

export interface CurrentUserContextValue {
  user: MeResponse | null
  isLoading: boolean
  /** True when a WS authz_denied refusal means the role has not been granted yet. */
  awaitingGrant: boolean
}

export const CurrentUserContext = createContext<
  CurrentUserContextValue | undefined
>(undefined)

/** Access the current user context. Must be used within CurrentUserProvider. */
export function useCurrentUserContext(): CurrentUserContextValue {
  const ctx = useContext(CurrentUserContext)
  if (ctx === undefined) {
    throw new Error('useCurrentUserContext must be used within CurrentUserProvider')
  }
  return ctx
}
